/**
 * @fileoverview Label - Form label component
 *
 * Purpose: Accessible form label with disabled state support
 * Dependencies: Radix UI Label primitive
 * Used by: All form fields, checkboxes, radio buttons
 *
 * Features:
 * - Properly associates with form controls
 * - Disabled state styling (peer-disabled pattern)
 * - Accessible (for/htmlFor attributes)
 * - Consistent typography
 *
 * @example
 * <Label htmlFor="email">Email</Label>
 * <Input id="email" type="email" />
 *
 * @example
 * // With disabled input
 * <Label htmlFor="disabled">Disabled Field</Label>
 * <Input id="disabled" disabled />
 */

"use client";

import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/misc";

const labelVariants = cva(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
);

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> &
    VariantProps<typeof labelVariants>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(labelVariants(), className)}
    {...props}
  />
));
Label.displayName = LabelPrimitive.Root.displayName;

export { Label };
